const activeLeaseByViewer = new WeakMap();

export class FindingModelVisibilityError extends Error {
  constructor(code, message, cause = null) {
    super(message);
    this.name = 'FindingModelVisibilityError';
    this.code = code;
    if (cause) this.cause = cause;
  }
}

function viewerModels(viewer) {
  if (typeof viewer.getAllModels === 'function') return [...(viewer.getAllModels() || [])];
  const queued = viewer.impl?.modelQueue?.()?.getModels?.();
  if (queued) return [...queued];
  return viewer.model ? [viewer.model] : [];
}

function containsOriginalModels(expected, actual) {
  return expected.every(model => actual.includes(model));
}

function visibilityError(code, message, cause = null) {
  return new FindingModelVisibilityError(code, message, cause);
}

export async function acquireFindingModelVisibility({ viewer, onCleanupError = () => {} }) {
  if (!viewer?.getState || !viewer?.hideAll || !viewer?.restoreState) {
    throw visibilityError(
      'MODEL_VISIBILITY_UNAVAILABLE',
      'The 3D model visibility state is unavailable.',
    );
  }
  if (activeLeaseByViewer.has(viewer)) {
    throw visibilityError(
      'MODEL_VISIBILITY_ALREADY_ACQUIRED',
      '3D model visibility is already owned by an active positioning flow.',
    );
  }
  const snapshot = viewer.getState({ objectSet: true });
  const models = viewerModels(viewer);
  const token = {};
  let released = false;
  activeLeaseByViewer.set(viewer, token);

  const assertModelIdentity = () => {
    if (!containsOriginalModels(models, viewerModels(viewer))) {
      throw visibilityError(
        'MODEL_VISIBILITY_IDENTITY_CHANGED',
        'The loaded 3D model changed while positioning was active.',
      );
    }
  };
  const hideModels = async () => {
    if (released || activeLeaseByViewer.get(viewer) !== token) return;
    assertModelIdentity();
    try {
      await viewer.hideAll();
      viewer.impl?.invalidate?.(true, true, true);
    } catch (err) {
      throw visibilityError(
        'MODEL_VISIBILITY_HIDE_FAILED',
        'The 3D model could not be hidden for point-cloud positioning.',
        err,
      );
    }
  };
  const restoreModels = async () => {
    assertModelIdentity();
    const restored = await viewer.restoreState(snapshot, { objectSet: true }, true);
    if (restored === false) {
      throw visibilityError(
        'MODEL_VISIBILITY_RESTORE_FAILED',
        'The prior 3D model visibility could not be restored.',
      );
    }
    viewer.impl?.invalidate?.(true, true, true);
  };
  const reportCleanupError = error => {
    try {
      onCleanupError(error);
    } catch {
      // Cleanup reporting must not replace the visibility failure.
    }
    return error;
  };

  try {
    await hideModels();
  } catch (err) {
    try {
      await restoreModels();
    } catch (cleanupErr) {
      err.cleanupError = reportCleanupError(cleanupErr instanceof FindingModelVisibilityError
        ? cleanupErr
        : visibilityError(
          'MODEL_VISIBILITY_RESTORE_FAILED',
          'The prior 3D model visibility could not be restored.',
          cleanupErr,
        ));
    }
    if (activeLeaseByViewer.get(viewer) === token) activeLeaseByViewer.delete(viewer);
    throw err;
  }

  return {
    rehide: hideModels,
    async release() {
      if (released) return null;
      released = true;
      if (activeLeaseByViewer.get(viewer) !== token) return null;
      try {
        await restoreModels();
        return null;
      } catch (err) {
        const cleanupError = err instanceof FindingModelVisibilityError
          ? err
          : visibilityError(
            'MODEL_VISIBILITY_RESTORE_FAILED',
            'The prior 3D model visibility could not be restored.',
            err,
          );
        return reportCleanupError(cleanupError);
      } finally {
        if (activeLeaseByViewer.get(viewer) === token) activeLeaseByViewer.delete(viewer);
      }
    },
  };
}
